import React, { useEffect, useRef, useState } from 'react';
import { OnChangeValue } from 'react-select';
import { useLazyQuery } from '@apollo/client';
import { uniqBy } from 'lodash';
import {
  Query,
  QueryUsersArgs,
  UserAvatar,
} from '@lgg/isomorphic/types/__generated__/graphql';
import {
  UsersRefetchType,
  COMPANY_USERS,
} from 'src/components/domain/users/hooks/use-company-users';
import { useCompanyUsersListForSelect } from 'src/components/domain/users/hooks/use-company-users-list-for-select';
import { ImageChip, TextChip } from 'src/components/general/display/chip';
import { entityToSelectOptionMapper } from 'src/components/general/inputs/select/mappers/entity-to-select-option-mapper';
import { Select, SelectOption } from 'src/components/general/inputs/select/select';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { getNodesFromConnection } from 'src/utils/graphql/get-nodes-from-connection';

const getAvatarChip = (avatar: UserAvatar): TextChip | ImageChip => {
  if (avatar.url) {
    return {
      url: avatar.url,
    };
  }

  return {
    text: avatar.initials,
    color: avatar.color,
  };
};

type CompanyUserSelectProps<IsMulti extends boolean> = {
  selectedUsersIds: number[];
  isMulti: IsMulti;
  isClearable?: boolean;
  forceCaret?: boolean;
  onChange: ValueChanged<OnChangeValue<SelectOption<number>, IsMulti>>;
  isCreatable?: boolean;
  name: string;
  placeholder?: string;
  mobileLabel?: string;
  label?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  hasError?: boolean;
  refetchRef?: (refetch: UsersRefetchType) => void;
  triggerOverride?: (
    value: OnChangeValue<SelectOption<number>, IsMulti>,
  ) => React.ReactNode;
  renderValue?: (value: SelectOption<number>) => React.ReactNode;
  hideIndicators?: boolean;
  renderPlaceholder?: () => React.ReactNode;
};

export const CompanyUsersSelect = <IsMulti extends boolean>(
  props: CompanyUserSelectProps<IsMulti>,
) => {
  const currentInstitution = useCurrentInstitution();
  const {
    isMulti,
    selectedUsersIds,
    isLoading,
    isCreatable,
    isDisabled,
    isClearable = true,
    forceCaret = false,
    hasError = false,
    refetchRef,
    triggerOverride,
    mobileLabel,
    renderValue,
    hideIndicators = false,
    renderPlaceholder,
    ...rest
  } = props;

  const [fetchExtraUsers] = useLazyQuery<Pick<Query, 'users'>, Partial<QueryUsersArgs>>(
    COMPANY_USERS,
    {
      fetchPolicy: 'no-cache',
      nextFetchPolicy: 'no-cache',
    },
  );
  const [cachedUserOptions, setCachedUserOptions] = useState<SelectOption<number>[]>([]);

  // Tracks ids we've already tried to fetch (found or not), so a selected id that
  // doesn't resolve to a user (e.g. deleted) doesn't get re-fetched on every render.
  const attemptedUserIdsRef = useRef<Set<number>>(new Set());

  // Tracks whether a search was performed while the menu was open, so closing it
  // without having searched doesn't trigger a redundant refetch with unchanged variables.
  const hasSearchedRef = useRef(false);

  // Whenever a selected id isn't part of cachedUserOptions yet (initial load, or the
  // selection changed to something the search/pagination hasn't surfaced), fetch it
  // explicitly so `value` can still resolve it.
  useEffect(() => {
    const missingIds = selectedUsersIds.filter(
      (id) =>
        !cachedUserOptions.some((option) => option.value === id) &&
        !attemptedUserIdsRef.current.has(id),
    );

    if (!missingIds.length) {
      return;
    }

    missingIds.forEach((id) => attemptedUserIdsRef.current.add(id));

    void (async () => {
      const { data } = await fetchExtraUsers({
        variables: {
          institutionId: currentInstitution.id,
          where: {
            id: { _in: missingIds },
            isActive: { _eq: true },
          },
        },
      });

      const nodes = getNodesFromConnection(data?.users);

      if (nodes.length) {
        setCachedUserOptions((prevState) => {
          return uniqBy(
            [
              ...prevState,
              ...nodes.map(({ id, fullName, avatar }) => ({
                ...entityToSelectOptionMapper({
                  id,
                  name: fullName,
                }),
                chip: avatar ? getAvatarChip(avatar) : undefined,
              })),
            ],
            'value',
          );
        });
      }
    })();
  }, [cachedUserOptions, currentInstitution.id, fetchExtraUsers, selectedUsersIds]);

  const {
    options: userOptions,
    groupedOptions,
    loading: loadingUserOptions,
    refetch,
    loadMore,
    hasNextPage,
  } = useCompanyUsersListForSelect({
    isActive: {
      _eq: true,
    },
  });

  useEffect(() => {
    if (userOptions.length) {
      setCachedUserOptions((prevState) => {
        return uniqBy([...prevState, ...userOptions], 'value');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingUserOptions]);

  useEffect(() => {
    refetchRef?.(refetch);
  }, [refetch, refetchRef]);

  return (
    <Select
      {...rest}
      isSearchable
      isMulti={isMulti}
      onChange={(value) => {
        rest?.onChange(value);
      }}
      onMenuClose={async () => {
        if (!hasSearchedRef.current) {
          return;
        }

        hasSearchedRef.current = false;
        await refetch({
          where: {
            isActive: { _eq: true },
          },
        });
      }}
      onSearch={async (name) => {
        hasSearchedRef.current = true;
        await refetch({
          where: {
            matchKeywords: { _all: name },
            isActive: { _eq: true },
          },
        });
      }}
      triggerOverride={triggerOverride}
      mobileLabel={mobileLabel}
      isClearable={isClearable}
      forceCaret={forceCaret}
      canLoadMore={hasNextPage}
      onLoadMore={loadMore}
      isLoading={loadingUserOptions || isLoading}
      isCreatable={isCreatable}
      isDisabled={isDisabled}
      hasError={hasError}
      renderValue={renderValue}
      hideIndicators={hideIndicators}
      value={
        selectedUsersIds
          .map((id) => {
            return cachedUserOptions.find((option) => option.value === id);
          })
          .filter(Boolean) as SelectOption<number>[]
      }
      options={groupedOptions}
      renderPlaceholder={renderPlaceholder}
    />
  );
};
